import type {
  MoveItem,
  PlannedMove,
  StorageTransferDisposition,
  StorageTransferMove,
} from '@/types/storage'

/**
 * Derivations behind the Replace unit wizard.
 *
 * Pure and dependency-free, for the reason the scheduling engine is: this is
 * the part that decides whether a plan can be approved, and it has to be
 * testable without a DOM. `vitest.config.ts` matches `src/**\/*.test.ts` only
 * — a `.tsx` test would silently never run — so anything worth asserting on
 * belongs here rather than in a component.
 */

/**
 * The shape both a previewed move and a persisted one satisfy.
 *
 * Structural rather than a union so the same table renders a plan that has not
 * been created yet and one read back from the API. `status` is absent on a
 * preview, which reads as PENDING — nothing has been excluded yet.
 */
export type ReviewableMove = Pick<
  PlannedMove,
  'ordinal' | 'sourceNodePath' | 'targetNodeRef' | 'targetNodePath' | 'conditionVerdict' | 'item'
> & {
  /** The lot being moved, so the location picker can offer slots at its condition. */
  lotId?: string | null
  productId?: string | null
  status?: StorageTransferMove['status']
  /** Set once the move was carried out or skipped at the freezer (z8nrz7e7ak). */
  scannedAt?: string | null
  skipReason?: string | null
}

/** What a row says when its lot or product was deleted after planning. */
export const ITEM_GONE = 'Item no longer exists'

/** The item's one-line name, or the fallback when it no longer exists. */
export function itemLabel(item: MoveItem | null): string {
  return item?.label ?? ITEM_GONE
}

/**
 * The identifier printed on the thing itself — the lot number for a lot, DIN
 * and description code for a product. It is what a person matches against the
 * label in their hand, so rows lead with it rather than burying it after the
 * material name, where a truncated cell cut it off.
 */
export function itemCode(item: MoveItem | null): string {
  if (!item) return ITEM_GONE
  if (item.kind === 'LOT') return item.lotNumber ?? item.label
  return item.label
}

/** Short badge text telling a product from a lot at a glance. */
export function itemKindLabel(item: MoveItem | null): string | null {
  if (!item) return null
  return item.kind === 'PRODUCT' ? 'Product' : 'Lot'
}

/**
 * The secondary line under the label: a product's name, or a lot's quantity
 * and expiry. Null when there is nothing to add, so the caller renders no
 * empty line.
 */
export function itemDetail(item: MoveItem | null): string | null {
  if (!item) return null
  if (item.kind === 'PRODUCT') return item.productName

  // The material is context; the lot number is the headline (`itemCode`).
  const parts: string[] = []
  if (item.materialName) parts.push(item.materialName)
  if (item.quantity !== null) parts.push(`Qty ${item.quantity}`)
  // The date part only: the stored value is midnight UTC, and a local-time
  // render would show the day before for anyone west of Greenwich.
  if (item.expiryDate) parts.push(`Exp ${item.expiryDate.slice(0, 10)}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** How a move names itself in a prompt or a message: the item, then where it is. */
export function moveName(move: Pick<ReviewableMove, 'item' | 'sourceNodePath'>): string {
  return `${itemLabel(move.item)} (${move.sourceNodePath})`
}

export type LocatedMove<M> = {
  move: M
  /** 1-based place among the moves still to do; null for an excluded move. */
  position: number | null
  /** How many moves are still to do in the plan. */
  of: number
  /** The next move to do after this one, in walk order, or null at the end. */
  nextId: string | null
}

/**
 * One move within its plan, for the screen a move's QR code opens.
 *
 * "3 of 40" and a way to the next one are what let a tech work a plan from the
 * codes alone without going back to the list. Excluded moves are not counted:
 * they are not on anybody's list to do.
 */
export function locateMove<M extends { id: string; ordinal: number; status?: string }>(
  moves: M[],
  moveId: string,
): LocatedMove<M> | null {
  const move = moves.find((m) => m.id === moveId)
  if (!move) return null

  const todo = moves.filter((m) => m.status !== 'SKIPPED').sort((a, b) => a.ordinal - b.ordinal)
  const index = todo.findIndex((m) => m.id === moveId)
  return {
    move,
    position: index === -1 ? null : index + 1,
    of: todo.length,
    nextId: index === -1 ? null : (todo[index + 1]?.id ?? null),
  }
}

/**
 * Why a move has no destination, in words (z8nrz7f2gh).
 *
 * The server puts the reason in `conditionVerdict` when it cannot place an
 * occupant ("No free space left in FRZ-02."), so the row can say that rather
 * than "nowhere yet". Null when there is nothing more specific to say.
 */
export function unplacedReason(move: Pick<ReviewableMove, 'targetNodeRef' | 'conditionVerdict'>): string | null {
  if (move.targetNodeRef) return null
  const v = move.conditionVerdict?.trim()
  return v && v !== 'ok' && v !== 'No destination' ? v : null
}

/**
 * A change a person made to one previewed row, before any draft exists
 * (z8nrz7f2gh). Keyed by ordinal while the dialog is open; sent to the server
 * by the occupant it carries, which is what the server matches on.
 */
export type PreviewEdit =
  | { kind: 'target'; targetNodeRef: string; targetNodePath: string }
  | { kind: 'exclude'; skipReason: string }

/** The preview with the person's edits laid over it, for the table and the blockers. */
export function applyPreviewEdits<M extends ReviewableMove>(
  moves: M[],
  edits: ReadonlyMap<number, PreviewEdit>,
): M[] {
  if (edits.size === 0) return moves
  return moves.map((m) => {
    const e = edits.get(m.ordinal)
    if (!e) return m
    if (e.kind === 'exclude') return { ...m, status: 'SKIPPED', skipReason: e.skipReason, scannedAt: null }
    return {
      ...m,
      targetNodeRef: e.targetNodeRef,
      targetNodePath: e.targetNodePath,
      // The server checks the slot again on create; a slot the picker offered
      // is already at the right condition.
      conditionVerdict: 'ok',
      status: undefined,
    }
  })
}

/** The edits as the create request carries them. */
export function previewEditsPayload(
  moves: Pick<ReviewableMove, 'ordinal' | 'lotId' | 'productId'>[],
  edits: ReadonlyMap<number, PreviewEdit>,
): { lotId?: string; productId?: string; targetNodeRef?: string; exclude?: boolean; skipReason?: string }[] {
  const out = []
  for (const m of moves) {
    const e = edits.get(m.ordinal)
    if (!e) continue
    const who = m.lotId ? { lotId: m.lotId } : { productId: m.productId ?? undefined }
    out.push(
      e.kind === 'exclude'
        ? { ...who, exclude: true, skipReason: e.skipReason }
        : { ...who, targetNodeRef: e.targetNodeRef },
    )
  }
  return out
}

/** Slots other live rows already head for — the picker must not offer them. */
export function claimedSlots(moves: ReviewableMove[], exceptOrdinal: number): Set<string> {
  const claimed = new Set<string>()
  for (const m of moves) {
    if (m.ordinal === exceptOrdinal || m.status === 'SKIPPED' || !m.targetNodeRef) continue
    claimed.add(m.targetNodeRef)
  }
  return claimed
}

/** At most three moves by name, then a count of the rest. */
function namedList(moves: ReviewableMove[]): string {
  const named = moves.slice(0, 3).map(moveName).join(', ')
  const rest = moves.length - 3
  return rest > 0 ? `${named} and ${rest} more` : named
}

export type MoveSummary = {
  total: number
  /** Has a destination and an acceptable verdict. */
  planned: number
  unmapped: number
  conditionFailures: number
  excluded: number
}

// A move left behind at the freezer is SKIPPED too, but it was planned and
// then carried out as a skip; only a draft exclusion has no scan time.
const isExcluded = (m: ReviewableMove) => m.status === 'SKIPPED' && !m.scannedAt

export function summariseMoves(moves: ReviewableMove[]): MoveSummary {
  const summary: MoveSummary = {
    total: moves.length,
    planned: 0,
    unmapped: 0,
    conditionFailures: 0,
    excluded: 0,
  }

  for (const m of moves) {
    // An exclusion is an answered question. Counting it as a problem would
    // keep the wizard's warning up for a decision somebody already made.
    if (isExcluded(m)) {
      summary.excluded += 1
      continue
    }
    if (!m.targetNodeRef) {
      summary.unmapped += 1
      continue
    }
    if (m.conditionVerdict !== 'ok') {
      summary.conditionFailures += 1
      continue
    }
    summary.planned += 1
  }

  return summary
}

export type Blocker = {
  kind: 'unmapped' | 'condition'
  count: number
  message: string
}

/**
 * What stands between this plan and approval.
 *
 * Both kinds are reported, never just the first: a person fixing them wants to
 * know the size of the job before they start, and the backend refuses on both
 * anyway.
 */
export function approvalBlockers(moves: ReviewableMove[]): Blocker[] {
  // The same classification `summariseMoves` makes, kept as rows rather than
  // counts so each message can name what it is about — "1 occupant" sends a
  // person hunting through the table for which one.
  const live = moves.filter((m) => !isExcluded(m)).sort((a, b) => a.ordinal - b.ordinal)
  const unmapped = live.filter((m) => !m.targetNodeRef)
  const unsuitable = live.filter((m) => m.targetNodeRef && m.conditionVerdict !== 'ok')
  const blockers: Blocker[] = []

  if (unmapped.length > 0) {
    blockers.push({
      kind: 'unmapped',
      count: unmapped.length,
      message:
        unmapped.length === 1
          ? `${moveName(unmapped[0])} has nowhere to go. Give it a destination, or exclude it with a reason.`
          : `${unmapped.length} occupants have nowhere to go: ${namedList(unmapped)}. Give each a destination, or exclude it with a reason.`,
    })
  }

  if (unsuitable.length > 0) {
    blockers.push({
      kind: 'condition',
      count: unsuitable.length,
      message:
        unsuitable.length === 1
          ? `${moveName(unsuitable[0])} would land somewhere unsuitable for it.`
          : `${unsuitable.length} moves would land somewhere unsuitable for what they hold: ${namedList(unsuitable)}.`,
    })
  }

  return blockers
}

export type MoveGroup<M extends ReviewableMove = ReviewableMove> = {
  /** The container path, e.g. "FRZ-01/R1/B1". */
  container: string
  moves: M[]
}

/** The container a position sits in — its path minus the last segment. */
function containerOf(path: string): string {
  const cut = path.lastIndexOf('/')
  // An occupant sitting directly in the unit has no container above it; its
  // own path is the most honest answer.
  return cut > 0 ? path.slice(0, cut) : path
}

/**
 * Moves grouped by the box they are coming out of, in walk order.
 *
 * A flat list of a thousand slots is unreviewable; a person checks a plan one
 * box at a time, which is also the order they will work it.
 *
 * Generic over the move shape (not fixed to `ReviewableMove`) so a caller
 * holding a richer type — e.g. a persisted `StorageTransferMove`, which also
 * carries `id` — gets that same type back rather than the narrowed one. The
 * print sheet (`sheetMoveGroups` in `src/lib/transferPrint.ts`) relies on this
 * to group "exactly as on the page" without losing `id` to the pick.
 */
export function groupMovesByContainer<M extends ReviewableMove>(moves: M[]): MoveGroup<M>[] {
  const groups = new Map<string, M[]>()

  for (const m of [...moves].sort((a, b) => a.ordinal - b.ordinal)) {
    const container = containerOf(m.sourceNodePath)
    const existing = groups.get(container)
    if (existing) existing.push(m)
    else groups.set(container, [m])
  }

  return [...groups.entries()].map(([container, grouped]) => ({ container, moves: grouped }))
}

/**
 * The disposition, in the words the approver needs.
 *
 * This is the one choice on the form with consequences that cannot be undone
 * later — a decommission deactivates the source's whole subtree and forecloses
 * the return leg — so it is stated as a sentence about this freezer rather
 * than as an enum name in a dropdown.
 */
export function dispositionStatement(
  disposition: StorageTransferDisposition,
  sourceCode: string,
): string {
  switch (disposition) {
    case 'RETURN_TO_SERVICE':
      return `${sourceCode} returns to service afterwards; you can plan the stock's return once this transfer completes.`
    case 'DECOMMISSION':
      return `${sourceCode} is retired permanently. Its layout is deactivated, and no return can be planned.`
    case 'REMAINS_IN_SERVICE':
      return `${sourceCode} stays in service. Only the positions in this plan are held; the rest of the unit keeps taking stock.`
  }
}
